import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CustomerAuthProvider } from '@/contexts/CustomerAuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CorporateLandingPage } from '@/pages/CorporateLandingPage';
import { MarketingLandingPage } from '@/pages/MarketingLandingPage';
import ProductsPage from '@/pages/ProductsPage';
import { ContactPage } from '@/pages/ContactPage';
import { BatchApp } from '@/pages/BatchApp';
import { AuthPage } from '@/pages/AuthPage';
import { AdminPortal } from '@/pages/AdminPortal';
import { CustomerPortal } from '@/pages/CustomerPortal';
import { SearchInputPage } from '@/pages/search/SearchInputPage';
import { SearchPaymentPage } from '@/pages/search/SearchPaymentPage';
import { SearchResultsPage } from '@/pages/search/SearchResultsPage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { PricingPage } from '@/pages/PricingPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CustomerAuthProvider>
          <Router>
            <div className="App">
              <Routes>
                <Route path="/" element={<CorporateLandingPage />} />
                <Route path="/marketing" element={<MarketingLandingPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route
                  path="/batch/*"
                  element={
                    <ErrorBoundary
                      fallbackTitle="Batch Processing Error"
                      fallbackMessage="An error occurred during batch processing. Your data is safe. Please try again or contact support."
                    >
                      <BatchApp />
                    </ErrorBoundary>
                  }
                />

                {/* Single Person Search Routes */}
                <Route path="/search" element={<SearchInputPage />} />
                <Route
                  path="/search/payment"
                  element={
                    <ErrorBoundary
                      fallbackTitle="Payment Error"
                      fallbackMessage="An error occurred during payment processing. Your card has not been charged. Please try again."
                    >
                      <SearchPaymentPage />
                    </ErrorBoundary>
                  }
                />
                <Route path="/search/results/:searchId" element={<SearchResultsPage />} />

                {/* Customer Portal Routes */}
                <Route path="/account/*" element={<CustomerPortal />} />

                {/* Admin Portal Routes */}
                <Route path="/admin/*" element={<AdminPortal />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </CustomerAuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
